import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_META, PRIORITY_META, TYPE_META, fmtDate } from "@/lib/brand";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, FolderKanban, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_CLS = {
  active: "bg-emerald-50 text-emerald-700",
  on_hold: "bg-amber-50 text-amber-700",
  completed: "bg-slate-100 text-slate-600",
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = user.role !== "client";
  const [project, setProject] = useState(null);
  const [newTask, setNewTask] = useState("");
  const [adding, setAdding] = useState(false);

  const load = () => api.get(`/projects/${id}`).then((r) => setProject(r.data));
  useEffect(() => { load(); }, [id]);

  const setStatus = async (status) => {
    const { data } = await api.patch(`/projects/${id}`, { status });
    setProject((p) => ({ ...p, status: data.status }));
    toast.success("Project updated");
  };

  const addTask = async () => {
    if (!newTask.trim()) return;
    setAdding(true);
    try {
      await api.post("/tickets", { title: newTask, type: "task", priority: "medium", client_id: project.client_id, project_id: id });
      setNewTask("");
      load();
      toast.success("Task added");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const removeProject = async () => {
    if (!window.confirm("Delete this project? Tasks will be unlinked but not deleted.")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Project deleted");
    navigate("/projects");
  };

  if (!project) return <div className="h-40 rounded-md bg-white border border-slate-200 animate-pulse" />;
  const pct = project.task_count ? Math.round((project.done_count / project.task_count) * 100) : 0;

  return (
    <div className="animate-fade-up max-w-4xl mx-auto space-y-6">
      <button onClick={() => navigate("/projects")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="back-projects-btn">
        <ArrowLeft size={15} /> Back to projects
      </button>

      <Card className="p-6 border-slate-200">
        <div className="flex items-start gap-3 flex-wrap">
          <FolderKanban size={26} style={{ color: "var(--tenant-primary)" }} />
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl font-bold text-slate-900">{project.name}</h1>
            <p className="text-xs text-slate-400 mt-1">{project.client_name} · created by {project.created_by_name} · Due {fmtDate(project.due_date)}</p>
            {project.description && <p className="text-sm text-slate-600 mt-3 whitespace-pre-wrap">{project.description}</p>}
          </div>
          <div className="flex items-center gap-2">
            <Select value={project.status} onValueChange={setStatus}>
              <SelectTrigger className="w-36 h-9" data-testid="project-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="on_hold">On hold</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            {isStaff && <button onClick={removeProject} className="text-slate-300 hover:text-red-600" data-testid="delete-project-btn"><Trash2 size={18} /></button>}
          </div>
        </div>
        <div className="mt-5">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-slate-500">{project.done_count}/{project.task_count} tasks completed</span>
            <span className="text-slate-400">{pct}%</span>
          </div>
          <Progress value={pct} className="h-2" />
        </div>
      </Card>

      <Card className="p-5 border-slate-200">
        <p className="font-display font-semibold text-slate-900 mb-3">Tasks ({project.tickets.length})</p>
        <div className="flex gap-2 mb-4">
          <Input value={newTask} onChange={(e) => setNewTask(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTask()} placeholder="Add a task…" data-testid="new-task-input" />
          <Button onClick={addTask} disabled={adding} className="text-white shrink-0" style={{ background: "var(--tenant-primary)" }} data-testid="add-task-btn">
            <Plus size={16} />
          </Button>
        </div>
        <div className="divide-y divide-slate-100 border border-slate-200 rounded-md">
          {project.tickets.map((t) => (
            <button key={t.id} onClick={() => navigate(`/tickets/${t.id}`)} data-testid="project-task-item"
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left">
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: STATUS_META[t.status].dot }} />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-800 truncate">{t.title}</p>
                <p className="text-[11px] text-slate-400">{TYPE_META[t.type]} · {STATUS_META[t.status].label}{t.assignee_name ? ` · ${t.assignee_name}` : ""}</p>
              </div>
              <span className={`text-[11px] px-2 py-0.5 rounded ${PRIORITY_META[t.priority].bg}`}>{PRIORITY_META[t.priority].label}</span>
            </button>
          ))}
          {project.tickets.length === 0 && <p className="text-center text-sm text-slate-400 py-8">No tasks yet. Add your first task above.</p>}
        </div>
      </Card>
    </div>
  );
}
